/* eslint-disable sonarjs/cognitive-complexity */
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { Question, Quotation, Thing, VideoObject, WithContext } from 'schema-dts';
import { twMerge } from 'tailwind-merge';

import { RichText } from '@boss/rich-text';
import type {
  ActionCardFields,
  Component,
  ContentPageFields,
  ImageFields,
  Theme,
  WidgetFields,
} from '@boss/types/b2b-b2c';
import {
  Accordion,
  ActionCard,
  CampaignBanner,
  Image as CfImage,
  ColorTool,
  ContentPageBanner,
  ContentTile,
  Fallback,
  Quote,
  Section,
  Video,
} from '@boss/ui';

import {
  ContactInformation,
  FormMapper,
  RecentlyViewedColors,
  getActionCardProps,
  getCampaignProps,
  getCardWrapperProps,
  getContactInfo,
  getContentPageBannerProps,
  getContentTileProps,
  getFaqProps,
  getFormMapperProps,
  getImageProps,
  getQuoteProps,
  getRichTextProps,
  getSectionProps,
  getStepByStepPlanProps,
  getVideoProps,
} from '../../';
import { COLOR_TOOL_BRAND } from '../../../constants/colorTool';
import { useCampaignEligibility, useContactPermissions } from '../../../hooks';
import { getEnvironmentName, isB2b } from '../../../utils';
import type { PointerColor, PointerProduct } from '../../../utils/cms';
import { mapContentfulToNextLocale } from '../../../utils/localeMapper';
import { componentsToExclude } from '../../../utils/options';
import { ColorButton, ProductCard } from '../../Contentful';
import ContactForm from '../../Form/ContactForm';
import InspirationCard from '../../InspirationCard/InspirationCard';
import CtaMapper from '../CtaMapper';
import MagazineCardMapper from '../MagazineCardMapper';

type Props = {
  entry: Component;
  theme?: Theme;
  inline?: boolean;
  locale: string;
  additionalProps?: Record<string, unknown>;
  embeddedEntry?: boolean;
  colors?: PointerColor[];
  products?: PointerProduct[];
  imageProps?: Record<string, unknown>;
};

const schemaString = 'https://schema.org';
/**
 * Dynamicly allocate schema.org data to the head
 */
const renderSchemaOrg = <T extends Thing>(schemaData: WithContext<T>) => (
  <Head>
    <script dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} type="application/ld+json" />
  </Head>
);

const widgetMapper = (entry: WidgetFields, locale: string) => {
  if (entry.widgetType === 'color-tool') {
    return <ColorTool brand={COLOR_TOOL_BRAND[mapContentfulToNextLocale(locale) as keyof typeof COLOR_TOOL_BRAND]} />;
  }

  if (entry.widgetType === 'corporate-contact-form') {
    return <ContactForm />;
  }

  if (entry.widgetType === 'viewed-colors') {
    return <RecentlyViewedColors />;
  }

  if (entry.widgetType === 'cookies-list') {
    return <div className="min-h-full" id="CookiebotDeclaration" />;
  }

  return null;
};

const imageMapper = (entry: ImageFields, additionalProps?: Record<string, unknown>) => {
  if (additionalProps?.parent === 'section') {
    return <CfImage {...getImageProps(entry)} {...additionalProps} className="max-h-1/2-screen min-h-full" />;
  }

  return <CfImage {...getImageProps(entry)} {...additionalProps} />;
};

// TODO: Consider a separate component for this, with a common interface
// and use CVA for formatting extra classes.
const EmbeddedEntryMapper = ({ entry, additionalProps, imageProps, ...rest }: Props) => {
  const type = entry?.__typename;

  // Excludes corrupt/unresolved embedded entries (e.g. missing reference in Contentful)
  if (!type) {
    return null;
  }

  if (type === 'image') {
    return (
      <span className={twMerge('h-112.5 my-5 block lg:my-12', imageProps?.wrapperClassName as string)}>
        <CfImage {...getImageProps(entry)} {...additionalProps} {...imageProps} />
      </span>
    );
  }

  return <Mapper additionalProps={additionalProps} entry={entry} {...rest} />;
};

const Mapper = ({
  entry,
  theme,
  locale,
  additionalProps,
  products,
  colors,
  inline,
  imageProps,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const { isCategoryTileEligible, isEligibilityLoaded, isCampaignEligible } = useCampaignEligibility(locale);
  // [45936] showprice=false hides every discount surface: action/campaign promo
  // tiles are dropped client-side (static pages keep the Contentful copy in
  // raw HTML — documented plan limitation).
  const { showprice = true } = useContactPermissions(locale) ?? {};

  // [45174] B2B-only guard shared by campaign-linked entries (action tiles and
  // content pages): hidden until the /discountinfos type=4 response has loaded,
  // then rendered only for eligible CampaignIds. On the colora (B2C) build the
  // hook is inert and callers skip the guard entirely, so B2C rendering is
  // unchanged. Orphaned ids (in the response, no entry in Contentful) render
  // nothing by construction — no error (AC-21). Entries without a linked
  // campaign are unaffected.
  //
  // [45175] Category tiles (ActionCard entries carrying a `categories` array)
  // are filtered all-or-nothing against that campaign's own Categories list:
  // a tile renders only when every linked category is present, and a tile with
  // zero configured categories always hides (AC-22). Campaign ineligibility is
  // transitive — a campaign absent from the response has no Categories list,
  // so every category check fails and the tile hides. Tiles without a
  // `categories` array keep the 45174 campaign rule.
  const isHiddenCampaign = (campaignId?: string) =>
    !!campaignId && (!isEligibilityLoaded || !isCampaignEligible(campaignId));

  const type = entry?.__typename;

  // Excludes corrupt/unresolved entries (e.g. missing reference in Contentful)
  if (!type) {
    return null;
  }

  const columns = [
    '1-column',
    '2-columns',
    '3-columns',
    '4-columns',
    '2-columns-left-smaller',
    '2-columns-right-smaller',
    '2-columns-quote',
    '2-columns-33-right',
    '2-columns-33-left',
  ];

  if (componentsToExclude.includes(type)) {
    return <Fallback componentType={type} env={getEnvironmentName()} type="exclude" />;
  }

  if (type === 'section') {
    return (
      <Section
        {...getSectionProps(entry, theme, locale, additionalProps?.titleClassName, products, colors)}
        {...additionalProps}
      />
    );
  }

  if (type === 'richText') {
    return <RichText {...getRichTextProps(entry, theme, locale)} {...additionalProps} imageProps={imageProps} />;
  }

  if (type === 'cta') {
    return (
      <CtaMapper
        additionalProps={additionalProps}
        columns={columns}
        entry={entry}
        inline={inline}
        locale={locale}
        theme={theme}
      />
    );
  }

  if (type === 'image') {
    return imageMapper(entry, { ...additionalProps, ...imageProps });
  }

  if (type === 'video') {
    return (
      <>
        {renderSchemaOrg<VideoObject>({
          '@context': schemaString,
          '@type': 'VideoObject',
          name: entry.alternativeText,
          description: entry.alternativeText,
          uploadDate: entry.createdAt,
          '@id': entry.externalVideoUrl,
        })}
        <Video {...getVideoProps(entry)} {...additionalProps} />
      </>
    );
  }

  if (type === 'faq') {
    return (
      <>
        {renderSchemaOrg<Question>({
          '@context': schemaString,
          '@type': 'Question',
          text: documentToPlainTextString(entry.question),
          acceptedAnswer: {
            '@type': 'Answer',
            text: documentToPlainTextString(entry.answer.content),
          },
        })}
        <Accordion aria-label="faq" variant="primary" {...getFaqProps(entry, theme, locale)} {...additionalProps} />
      </>
    );
  }

  if (type === 'quote') {
    return (
      <>
        {renderSchemaOrg<Quotation>({
          '@context': schemaString,
          '@type': 'Quotation',
          text: entry.quote,
          spokenByCharacter: {
            '@type': 'Person',
            name: entry.author,
          },
        })}
        <Quote {...getQuoteProps(entry)} {...additionalProps} />
      </>
    );
  }

  if (type === 'product') {
    return <ProductCard id={entry.productID} {...additionalProps} />;
  }

  if (type === 'widget') {
    return widgetMapper(entry, locale);
  }

  if (type === 'campaign') {
    // [45936] showprice=false hides every discount surface: campaign banners
    // with discount messaging are dropped client-side (documented plan
    // limitation for static HTML).
    if (showprice === false) {
      return null;
    }

    return <CampaignBanner {...getCampaignProps(entry, theme, locale)} />;
  }

  if (entry.__typename === 'contentPageBanner') {
    return <ContentPageBanner {...getContentPageBannerProps(entry, theme, locale)} />;
  }

  if (type === 'contentPage' || type === 'overviewPage') {
    // [45174] Content pages (B2B build only) can carry a campaign via the
    // Topic:Action custom app; the same eligibility rule as campaign tiles
    // applies when such a page is nested in a section's content field. The
    // generated Contentful types predate the campaign field — app-local cast,
    // same shape as ActionCardFields. Overview pages have no campaign field.
    const contentPageCampaignId =
      type === 'contentPage'
        ? (entry as ContentPageFields & { campaign?: { CampaignId?: string } }).campaign?.CampaignId
        : undefined;

    if (isB2b && isHiddenCampaign(contentPageCampaignId)) {
      return null;
    }

    return (
      <ContentTile
        {...getContentTileProps(entry, locale)}
        translations={{ linkLabel: t('goTo') }}
        whitespace={additionalProps?.parentDisplay === '1-column' ? 'none' : 'ratio'}
      />
    );
  }

  if (type === 'form') {
    return <FormMapper {...getFormMapperProps(entry)} />;
  }

  if (type === 'inspirationImage') {
    return <InspirationCard entry={entry} />;
  }

  if (type === 'contactInformation') {
    return <ContactInformation {...getContactInfo(entry, locale)} />;
  }

  if (type === 'action') {
    const campaignId = (entry as ActionCardFields).campaign?.CampaignId;
    const categories = (entry as ActionCardFields).categories;

    // [45936] showprice=false hides every discount surface: action promo tiles
    // are dropped client-side (documented plan limitation for static HTML).
    if (showprice === false) {
      return null;
    }

    if (Array.isArray(categories)) {
      // [45175] Category tile: default-hide until the response loads, then show
      // only when every linked category is in this campaign's own list. An
      // absent/undefined campaign id resolves to '' and therefore hides.
      if (
        isB2b &&
        (!isEligibilityLoaded ||
          !isCategoryTileEligible(
            campaignId ?? '',
            categories.map(category => category.Category),
          ))
      ) {
        return null;
      }
    } else if (isB2b && isHiddenCampaign(campaignId)) {
      // [45174] Campaign-only tile: unchanged campaign-eligibility rule.
      return null;
    }

    return <ActionCard {...getActionCardProps(entry, locale)} className="max-w-[30em]" />;
  }

  if (type === 'magazine') {
    return <MagazineCardMapper entry={entry} locale={locale} />;
  }

  if (type === 'cardWrapper') {
    return (
      <div className={entry.variation === 'wide' ? 'md:col-span-2' : ''}>
        <ContentTile
          {...getCardWrapperProps(entry, locale)}
          translations={{ linkLabel: t('goTo') }}
          whitespace={additionalProps?.parentDisplay === '1-column' ? 'none' : 'ratio'}
        />
      </div>
    );
  }

  if (type === 'color') {
    return (
      <span className={inline ? 'md:w-35 w-25 inline-block px-5 py-4' : ''}>
        <ColorButton id={entry.colorId} onClickAction="navigate" slug={entry.slug} />
      </span>
    );
  }

  if (type === 'stepByStepPlan') {
    return (
      <ContentTile
        {...getStepByStepPlanProps(entry, locale)}
        translations={{ linkLabel: t('goTo') }}
        whitespace={additionalProps?.parentDisplay === '1-column' ? 'none' : 'ratio'}
      />
    );
  }

  return <Fallback componentType={type} env={getEnvironmentName()} type="not-implemented" />;
};

const ComponentMapper = ({ inline, embeddedEntry, ...props }: Props) => {
  let body = null;

  if (embeddedEntry) {
    body = <EmbeddedEntryMapper {...props} />;
  } else {
    body = <Mapper {...props} inline={inline} />;
  }

  return inline ? <span className="inline">{body}</span> : body;
};

export default ComponentMapper;
